import type { ICampaign } from '../redux/buzzbridge/types';

const toNonNegativeInt = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
        return Math.floor(value);
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value);
        if (Number.isFinite(n) && n >= 0) return Math.floor(n);
    }
    return undefined;
};

type CampaignCapacityFields = Partial<
    Pick<
        ICampaign,
        | 'participants_count'
        | 'total_participants_count'
        | 'filled_positions'
        | 'positions_available'
        | 'active_participants'
        | 'is_at_capacity'
        | 'participants'
    >
>;

/** Total creator slots for the campaign (null = unknown / unlimited). */
export function getUgcCampaignMaxSlots(campaign: CampaignCapacityFields | null | undefined): number | null {
    if (!campaign) return null;
    const raw = toNonNegativeInt(campaign.total_participants_count) ?? toNonNegativeInt(campaign.participants_count);
    if (raw == null || raw <= 0) return null;
    return raw;
}

/** Approved / active slots consumed. */
export function getUgcCampaignFilledCount(campaign: CampaignCapacityFields | null | undefined): number {
    if (!campaign) return 0;
    const fromApi = toNonNegativeInt(campaign.filled_positions) ?? toNonNegativeInt(campaign.active_participants);
    if (fromApi != null) return fromApi;
    // When a seat cap exists, participants_count is the filled roster counter.
    if (toNonNegativeInt(campaign.total_participants_count) != null) {
        return toNonNegativeInt(campaign.participants_count) ?? campaign.participants?.length ?? 0;
    }
    return campaign.participants?.length ?? toNonNegativeInt(campaign.participants_count) ?? 0;
}

export function getUgcCampaignOpenSlots(campaign: CampaignCapacityFields | null | undefined): number {
    if (!campaign) return 0;
    const fromApi = toNonNegativeInt(campaign.positions_available);
    if (fromApi != null) return fromApi;
    const max = getUgcCampaignMaxSlots(campaign);
    if (max == null) return 0;
    return Math.max(0, max - getUgcCampaignFilledCount(campaign));
}

export function isUgcCampaignAtCapacity(campaign: CampaignCapacityFields | null | undefined): boolean {
    if (!campaign) return false;
    if (campaign.is_at_capacity === true) return true;
    const max = getUgcCampaignMaxSlots(campaign);
    if (max == null) return false;
    return getUgcCampaignFilledCount(campaign) >= max;
}

export function getUgcCampaignCapacityPercent(campaign: CampaignCapacityFields | null | undefined): number {
    const max = getUgcCampaignMaxSlots(campaign);
    if (max == null || max <= 0) return 0;
    const filled = getUgcCampaignFilledCount(campaign);
    return Math.min(100, Math.round((filled / max) * 100));
}
